// THE HAAL PROJECT — FISSION DATA
// IRL Variation 2 (Pre-Production) — 8-Layer Recognition Pathway
// FISSION_SEQ · EXTENDED_NAMES · HAWAS · BIOME_LABELS

export interface FissionEncounter {
  id: string;
  seq: number;
  title: string;
  loc: string;
  cat: 'Creation' | 'Process';
  biome: string;
  bg: string;
  type: 'eco';
  ayah: string;
  names: string[];
  encounter: string;
  threeQ: {
    is: string;
    what: string;
    when: string;
  };
  names_detail: Array<{
    name: string;
    ar: string;
    why: string;
  }>;
  sunnat: string[];
  zahir: string;
  batin: string;
  haqq: string;
  amanah: string;
  bridges: Array<{
    id: string;
    label: string;
    type: 'name' | 'cycle' | 'process' | 'amanah';
  }>;
}

export interface FissionName {
  id: string;
  ar: string;
  en: string;
  m: string;
  cat: string;
  ayat: string[];
  procs: string[];
  field: string;
  sim: string;
}

export interface HawasEntry {
  id: string;
  icon: string;
  label: string;
  ar: string;
  desc: string;
}

// ─── BIOME LABELS ─────────────────────────────────────────────────────────────

export const BIOME_LABELS: Record<string, string> = {
  forest: 'Forest',
  wetland: 'Wetland/Marsh',
  prairie: 'Tallgrass Prairie',
  confluence: 'River Confluence',
  piedmont_coast: 'Piedmont-Coastal',
  bottomland: 'River Bottomland',
  rainforest: 'Temperate Rainforest',
  bluffs: 'River Bluffs',
  plains: 'Great Plains',
  physics: 'Physics Layer',
  quantum: 'Quantum Layer',
  cross_biome: 'Cross-Biome',
  savannah: 'Savannah',
  coastal: 'Coastal',
  desert: 'Desert',
  ocean: 'Ocean',
  geological: 'Geological',
};

// ─── HAWAS — 5 Senses for Observation Panel ──────────────────────────────────

export const HAWAS: HawasEntry[] = [
  { id: 'basar', icon: '👁', label: 'Basar', ar: 'بصر', desc: 'Sight' },
  { id: 'sama', icon: '👂', label: 'Sama', ar: 'سمع', desc: 'Sound' },
  { id: 'shamm', icon: '🌿', label: 'Shamm', ar: 'شم', desc: 'Scent' },
  { id: 'dhawq', icon: '◉', label: 'Dhawq', ar: 'ذوق', desc: 'Taste' },
  { id: 'lams', icon: '✋', label: 'Lams', ar: 'لمس', desc: 'Touch' },
];

// ─── EXTENDED NAMES — 16 additional Names for V2 ─────────────────────────────

export const FISSION_EXTENDED_NAMES: FissionName[] = [
  {
    id: 'wahhab', ar: 'الوهّاب', en: 'Al-Wahhab', m: 'The Bestower', cat: 'Community',
    ayat: ['Indeed, You are the Bestower. (3:8)'],
    procs: ['Unconditional gift', 'Mycorrhizal nutrient transfer', 'Parental provision'],
    field: 'Where do you see gifts given without expectation of return?',
    sim: 'The mother tree feeding seedlings AND rain falling on barren land AND the gift given in secret.',
  },
  {
    id: 'muhaymin', ar: 'المهيمن', en: 'Al-Muhaymin', m: 'The Guardian', cat: 'Health',
    ayat: ['He is Allah, the Sovereign, the Pure, the Perfection, the Bestower of Faith, the Guardian. (59:23)'],
    procs: ['Migratory protection', 'Immune system', 'Stomatal regulation'],
    field: 'Where do you see protection operating invisibly?',
    sim: 'The monarch\'s extended lifespan for migration AND the immune system AND the stomata closing against drought.',
  },
  {
    id: 'badi', ar: 'البديع', en: 'Al-Badi', m: 'The Originator', cat: 'Education',
    ayat: ['Originator of the heavens and the earth. (2:117)'],
    procs: ['First seed', 'Novel species', 'Original design'],
    field: 'Where do you see something with no precedent?',
    sim: 'The first flowering plant AND the first eye AND the first community organization in a new city.',
  },
  {
    id: 'hafiz', ar: 'الحفيظ', en: 'Al-Hafiz', m: 'The Preserver', cat: 'Community',
    ayat: ['Indeed, Allah is Guardian over all things. (11:57)'],
    procs: ['Seed dormancy', 'Fossil preservation', 'Genetic memory'],
    field: 'Where do you see preservation across time?',
    sim: 'The lotus seed viable after 1,300 years AND the fossil AND the oral tradition.',
  },
  {
    id: 'muqaddim', ar: 'المقدّم', en: 'Al-Muqaddim', m: 'The Expediter', cat: 'Community',
    ayat: ['He is the First and the Last. (57:3)'],
    procs: ['Tidal punctuality', 'Seed timing', 'Migration timing'],
    field: 'Where do you see perfect timing?',
    sim: 'The tide arriving on schedule AND the seed germinating at the right moment AND the bird arriving at the right season.',
  },
  {
    id: 'jabbar', ar: 'الجبّار', en: 'Al-Jabbar', m: 'The Compeller', cat: 'Community',
    ayat: ['He is Allah, the Creator, the Evolver, the Fashioner. (59:24)'],
    procs: ['Gravitational compulsion', 'Tectonic force', 'Lightning discharge'],
    field: 'Where do you see irresistible force?',
    sim: 'Gravity compelling every particle AND tectonic uplift AND the lightning that cannot be stopped.',
  },
  {
    id: 'khabir', ar: 'الخبير', en: 'Al-Khabir', m: 'The All-Aware', cat: 'Education',
    ayat: ['Indeed, Allah is Acquainted with what you do. (49:13)'],
    procs: ['Chemical signaling', 'Ecological awareness', 'Root sensing'],
    field: 'Where do you see awareness without visible perception?',
    sim: 'The acacia chemical alarm AND the root sensing water AND the immune system identifying pathogens.',
  },
  {
    id: 'ghani', ar: 'الغني', en: 'Al-Ghani', m: 'The Self-Sufficient', cat: 'Community',
    ayat: ['Allah is Free of need, and you are the needy. (47:38)'],
    procs: ['Nitrogen fixation', 'Autotrophy', 'Self-sustaining systems'],
    field: 'Where do you see self-sufficiency in creation?',
    sim: 'The acacia fixing its own nitrogen AND the photosynthesizing leaf AND the community that produces its own food.',
  },
  {
    id: 'shakur', ar: 'الشكور', en: 'Ash-Shakur', m: 'The Appreciative', cat: 'Community',
    ayat: ['Indeed, He is Forgiving and Appreciative. (35:30)'],
    procs: ['Pollination reward', 'Mutualism', 'Reciprocal provision'],
    field: 'Where do you see reciprocal appreciation in creation?',
    sim: 'The date palm rewarding the pollinator AND the mycorrhizal exchange AND the community that honors its elders.',
  },
  {
    id: 'qayyum', ar: 'القيّوم', en: 'Al-Qayyum', m: 'The Self-Sustaining', cat: 'Health',
    ayat: ['Allah — there is no deity except Him, the Ever-Living, the Sustainer of existence. (2:255)'],
    procs: ['Tidal rhythm', 'Orbital mechanics', 'Circadian rhythm'],
    field: 'Where do you see self-sustaining systems?',
    sim: 'The tidal rhythm for 4.5 billion years AND the orbit AND the heartbeat.',
  },
  {
    id: 'matin', ar: 'المتين', en: 'Al-Matin', m: 'The Firm', cat: 'Community',
    ayat: ['Indeed, it is Allah who is the Provider, the Firm possessor of strength. (51:58)'],
    procs: ['Nuclear binding', 'Structural integrity', 'Electromagnetic cohesion'],
    field: 'Where do you see firmness that cannot be broken?',
    sim: 'The strong nuclear force AND the electromagnetic force making matter solid AND the community that holds through every storm.',
  },
  {
    id: 'samad', ar: 'الصمد', en: 'As-Samad', m: 'The Eternal Absolute, The Self-Sufficient', cat: 'Community',
    ayat: ['Allah, As-Samad, The Eternal, The Absolute. (112:2)'],
    procs: ['Nuclear self-sufficiency', 'Closed systems', 'Self-contained processes'],
    field: 'Where do you see absolute self-sufficiency?',
    sim: 'The nucleus requiring no external input AND the seed containing everything for germination AND the community that needs nothing from outside to begin.',
  },
  {
    id: 'muqit', ar: 'المقيت', en: 'Al-Muqit', m: 'The Sustainer', cat: 'Health',
    ayat: ['And Allah is over all things a Keeper. (4:85)'],
    procs: ['Energy conservation', 'First Law of Thermodynamics', 'Nutrient cycling'],
    field: 'Where do you see perfect accounting of energy?',
    sim: 'The First Law of Thermodynamics AND the nutrient cycle AND the community budget that accounts for every resource.',
  },
  {
    id: 'mumit', ar: 'المميت', en: 'Al-Mumit', m: 'The Giver of Death', cat: 'Community',
    ayat: ['He gives life and causes death. (57:2)'],
    procs: ['Radioactive decay', 'Apoptosis', 'Ecological succession'],
    field: 'Where do you see death serving life?',
    sim: 'Radioactive decay AND programmed cell death AND the forest fire that clears for new growth.',
  },
  {
    id: 'malik', ar: 'الملك', en: 'Al-Malik', m: 'The Sovereign', cat: 'Community',
    ayat: ['Sovereign of the Day of Recompense. (1:4)'],
    procs: ['Ecological governance', 'Atmospheric sovereignty', 'Gravitational dominion'],
    field: 'Where do you see sovereignty in creation?',
    sim: 'Gravity governing every particle AND the atmosphere governing climate AND the watershed governing all water within it.',
  },
  {
    id: 'ahad', ar: 'الأحد', en: 'Al-Ahad', m: 'The One, The Singularity', cat: 'Community',
    ayat: ['Say: He is Allah, Ahadun, Singularity. (112:1)'],
    procs: ['Absolute singularity', 'The only affirmative use of Ahad in Arabic literature', 'All multiplicity points to The One'],
    field: 'What in your field of vision has no comparable? What uniqueness points to The Singular?',
    sim: 'The only affirmative use of Ahad in all Arabic literature AND the singularity of each snowflake AND the unique fingerprint.',
  },
  {
    id: 'haseeb', ar: 'الحسيب', en: 'Al-Haseeb', m: 'The Reckoner', cat: 'Community',
    ayat: ['Indeed, Allah is ever, over all things, an Accountant. (4:86)', 'Sufficient is Allah as Reckoner. (33:39)'],
    procs: ['Carbon accounting in ecosystems', 'Nutrient cycling balance', 'Ecological carrying capacity'],
    field: 'Where do you see precise accounting in creation — nothing wasted, nothing unaccounted for?',
    sim: 'The carbon cycle accounting every molecule AND the ecosystem\'s carrying capacity AND the community that tracks its resources.',
  },
  {
    id: 'raqib', ar: 'الرقيب', en: 'Ar-Raqib', m: 'The Watchful', cat: 'Community',
    ayat: ['Indeed, Allah is ever, over you, an Observer. (4:1)', 'And Allah is Witness over all things. (58:6)'],
    procs: ['Ecological monitoring', 'Predator-prey observation', 'Sentinel species'],
    field: 'Where do you see watchfulness operating in creation — the sentinel, the observer, the guardian?',
    sim: 'The sentinel bird watching for predators AND the immune system monitoring for pathogens AND the community elder watching over the young.',
  },
];

// ─── FISSION_SEQ — 33 Encounters ─────────────────────────────────────────────

export const FISSION_SEQ: FissionEncounter[] = [
  {
    id: 's1', seq: 1, title: 'Big Bluestem', loc: 'Chicago', cat: 'Creation', biome: 'prairie', bg: '#2a3a1a', type: 'eco',
    ayah: 'And the earth — We spread it out and placed therein firmly set mountains and caused to grow therein something of every well-balanced pair. (50:7)',
    names: ['khaliq', 'sabur', 'qawi', 'batin'],
    encounter: 'Walking through a remnant prairie in the Midwest, a grass stands 8 feet tall, swaying in wind that would snap lesser plants. Its roots reach 12 feet below the surface — deeper than the visible blade is tall.',
    threeQ: {
      is: 'Yes, this grass exists as a biological organism, a provision, an ayah of Al-Khaliq, and a trust. It has been here, in some form, for 10,000 years.',
      what: 'Andropogon gerardii. Height (taqdir from Al-Musawwir), flexibility (taqdir), deep root system (taqdir), C4 photosynthesis (taqdir). None inherent to grass — measurements given specifically to this creation.',
      when: 'In spring: green shoots. Summer: 8-foot blades. Autumn: seed heads. Winter: standing dead material for the fire that will renew it. Further: 10,000 years of root death building the deepest topsoil in North America.',
    },
    names_detail: [
      { name: 'Al-Khaliq', ar: 'الخالق', why: 'Created a grass whose underground reality exceeds its visible reality by 4x' },
      { name: 'As-Sabur', ar: 'الصبور', why: 'Roots that took 10,000 years to build what 50 years of agriculture spent' },
      { name: 'Al-Qawi', ar: 'القوي', why: 'Stems flex in 60mph wind without breaking — strength through flexibility' },
      { name: 'Al-Batin', ar: 'الباطن', why: 'The 12-foot root is the batin; the 8-foot blade is the zahir. What we call grassland is actually a rootland' },
    ],
    sunnat: [
      'Dead material accumulates (fuel)',
      'Fire ignites (Al-Qahhar subdues what must be cleared)',
      'Above-ground biomass burns (destruction of the zahir)',
      'Root crowns survive below ground (the batin persists)',
      'Ash deposits nutrients (Al-Ghafar — accumulated debt of dead seasons cleared)',
      'Rain falls on cleared ground (Ar-Rahman\'s mercy after difficulty)',
      'Explosive regrowth from protected roots (Al-Muhyi — life from apparent death)',
    ],
    zahir: 'A tall grass waving in the wind',
    batin: '80% of the prairie\'s biomass is underground. The grassland you walk on is a rootland. The name we give it — grass-land — is itself evidence of how we name from the zahir and miss the haqq.',
    haqq: 'This grass is an ayah declaring that what is hidden exceeds what is visible, that patience builds what haste cannot spend, that strength comes through flexibility, and that periodic destruction serves creation.',
    amanah: 'If the caretaker recognizes this grass as Rizq from Ar-Razzaq — the topsoil it built feeding the nation\'s breadbasket — then the trust includes: what happened to this prairie? The MRG\'s food security resources meet this recognition.',
    bridges: [
      { id: 's19', label: 'Savannah Acacia (enrichment parallel)', type: 'name' },
      { id: 's12', label: 'Omaha: Loess Hills (grass stabilization)', type: 'cycle' },
    ],
  },
  {
    id: 's2', seq: 2, title: 'Wetland Filtration', loc: 'Detroit', cat: 'Process', biome: 'wetland', bg: '#0c3238', type: 'eco',
    ayah: 'And We send down from the sky pure water. (25:48)',
    names: ['quddus', 'hakeem', 'rafi'],
    encounter: 'Water enters murky, exits clear. One acre filters what a $500,000 treatment plant filters.',
    threeQ: {
      is: 'Yes — this wetland exists as a biological process, an Ayah of Al-Quddus, and a trust placed in this watershed.',
      what: 'Phragmites, cattail, sedge, and microbial communities. Each species removes a different contaminant at a different stage. The sequence is the wisdom.',
      when: 'Continuously, for as long as the wetland exists. The process began when the first plant colonized the margin. It will end when the wetland is drained.',
    },
    names_detail: [
      { name: 'Al-Quddus', ar: 'القدوس', why: 'Purification manifest. Slow passage through the right medium transforms the substance.' },
      { name: 'Al-Hakeem', ar: 'الحكيم', why: 'Different contaminants removed at different stages in precise sequence' },
    ],
    sunnat: [
      'Polluted inflow enters',
      'Velocity reduces — patience is the first stage',
      'Heavy sediments settle (Al-Muqsit)',
      'Microbes digest organics (Al-Latif)',
      'Plant roots uptake metals',
      'UV neutralizes pathogens (An-Nur)',
      'Clean water exits (Al-Quddus)',
    ],
    zahir: 'A marshy area with reeds and murky water',
    batin: 'A $500,000 treatment plant operating for free. The most sophisticated water purification technology on Earth, running on sunlight and biology.',
    haqq: 'This wetland is an ayah declaring that purification requires patience and that creation outperforms human engineering.',
    amanah: 'Detroit\'s water quality issues and wetlands that could address them. Wetland restoration is community health Islah.',
    bridges: [
      { id: 's31', label: 'Water\'s Anomalous Properties (molecular basis)', type: 'process' },
    ],
  },
  {
    id: 's3', seq: 3, title: 'Canopy Photosynthesis', loc: 'Atlanta', cat: 'Process', biome: 'forest', bg: '#1c5a3e', type: 'eco',
    ayah: 'Allah is the Light of the heavens and the earth. (24:35)',
    names: ['nur', 'razzaq', 'qadir', 'muhyi', 'hakeem'],
    encounter: 'Standing in the Piedmont forest. Light penetrates the canopy in shifting columns. Leaves above are running the most sophisticated energy conversion process on Earth.',
    threeQ: {
      is: 'This process exists: 10¹⁵ divine commands executing per second per leaf. The forest canopy is not passively receiving light — it is actively converting An-Nur\'s provision into Ar-Razzaq\'s provision in a different form.',
      what: 'The leaf sequence: the sunlight that comes to the leaf is Rizq — Allah is Ar-Razzaq. The sun doesn\'t inherently own light. The sunlight comes from Allah because Allah is An-Nur.',
      when: 'Spring: ramping up as leaves unfurl. Summer: peak operation. Autumn: shutting down, chlorophyll breaking down to reveal carotenoids that were always there — Al-Batin made zahir. Winter: process suspended but potential remains.',
    },
    names_detail: [
      { name: 'An-Nur', ar: 'النور', why: 'The photon is not the sun\'s property; it is rizq from An-Nur traveling 93 million miles in 8 minutes to arrive at this specific leaf' },
      { name: 'Ar-Razzaq', ar: 'الرزاق', why: 'Light converted to glucose. One form of rizq becomes another. The realization IS itself rizq from the Names of Allah' },
      { name: 'Al-Qadir', ar: 'القدرة', why: 'The only One with the power to give these measurements. The electron transport chain, the Calvin cycle — all taqdir' },
      { name: 'Al-Muhyi', ar: 'المحيي', why: 'Dead matter (CO₂, H₂O, light) becomes living tissue (glucose, oxygen). Life from non-life' },
      { name: 'Al-Hakeem', ar: 'الحكيم', why: 'Each canopy layer receives exactly what it needs. Al-Muqsit in light allocation' },
    ],
    sunnat: [
      'Anbit! (Grow!) — photon captured by chlorophyll',
      'Electron excited — energy level raised (Ar-Rafi)',
      'Water split — H₂O broken into H⁺, electrons, O₂ (the oxygen you breathe is a byproduct)',
      'Electron transport chain generates ATP',
      'Arzuq! (Provide!) — Calvin cycle synthesizes glucose',
      'Glucose distributed throughout the tree (Al-Muqsit)',
      'Oxygen released to the atmosphere (provision flows outward to every breathing creature)',
    ],
    zahir: 'Green leaves in sunlight',
    batin: '10¹⁵ atomic-scale divine commands per second; the green color itself is the rejected wavelength — the provision is hidden in the absorbed light you cannot see. The color of divine provision is invisible.',
    haqq: 'Every leaf is an ayah declaring Light upon Light. The leaf is not making food; it is executing the amr of Ar-Razzaq at the direction of An-Nur by the taqdir of Al-Musawwir.',
    amanah: 'If the caretaker recognizes this as the Rizq of Ar-Razzaq, then deforestation is disruption of a divine provision system. The MRG\'s community resources meet this through urban forestry and environmental justice organizations.',
    bridges: [
      { id: 's1', label: 'Chicago: C4 Prairie Photosynthesis (same command, different dialect)', type: 'process' },
      { id: 's27', label: 'Electromagnetism: An-Nur at deepest resolution', type: 'process' },
      { id: 's33', label: 'Quantum coherence: 95% efficiency through superposition', type: 'process' },
    ],
  },
  {
    id: 's4', seq: 4, title: 'Mycorrhizal Network', loc: 'Atlanta', cat: 'Process', biome: 'forest', bg: '#0e2a16', type: 'eco',
    ayah: 'And there is not a thing but that it glorifies Him with praise. (17:44)',
    names: ['wadud', 'hakeem', 'latif', 'wahhab'],
    encounter: '10,000 trees connected through fungal hyphae. The underground divine internet. Al-Wadud pulses through every nutrient exchange.',
    threeQ: {
      is: 'Yes — this network exists as a biological process, an Ayah of Al-Wadud, and a trust placed in this forest.',
      what: 'Fungal hyphae 1/10th the diameter of a root hair. The network exchanges carbon, water, phosphorus, and chemical signals between trees of different species.',
      when: 'Continuously, since the first trees colonized the land. The network predates the forest. It will outlast individual trees.',
    },
    names_detail: [
      { name: 'Al-Wadud', ar: 'الودود', why: 'Mother trees feed seedlings through fungal intermediaries. The network operates on love as its protocol.' },
      { name: 'Al-Latif', ar: 'اللطيف', why: 'Hyphae 1/10th the diameter of a root hair. Invisible yet sustaining every visible tree.' },
    ],
    sunnat: [
      'Spore lands on soil (Al-Fattah opens)',
      'Hypha extends toward root (Al-Hadi guides)',
      'Root allows penetration (Al-Hakeem distinguishes friend from invader)',
      'Arbuscules form (Al-Musawwir maximizes exchange surface)',
      'Nutrient exchange begins (Ar-Razzaq in both directions)',
      'Network extends to more trees (Al-Jami)',
      'Chemical communication activates (Al-Khabir)',
      'Mother tree recognition (Al-Wadud)',
    ],
    zahir: 'A forest floor with mushrooms and roots',
    batin: '10,000 trees connected through a single network. The forest is not a collection of individuals — it is one organism with many expressions.',
    haqq: 'Creation was designed for cooperation. The most essential systems are invisible. What we name as separate are unified through hidden connections.',
    amanah: 'Development severs connections that took decades. A parking lot destroys a communication network. Protect the networks — underground and aboveground.',
    bridges: [
      { id: 's1', label: 'Chicago: Prairie Root Depth (underground carbon)', type: 'name' },
      { id: 's23', label: 'Coral Reef: zooxanthellae symbiosis (intimate partnership)', type: 'name' },
    ],
  },
  {
    id: 's5', seq: 5, title: 'Amphibian Metamorphosis', loc: 'Detroit', cat: 'Process', biome: 'wetland', bg: '#0c2e34', type: 'eco',
    ayah: 'He creates you in the wombs of your mothers, creation after creation. (39:6)',
    names: ['bari', 'muhawwil', 'khaliq'],
    encounter: 'Same DNA, four radically different body plans. Gills become lungs, tail becomes legs. This IS huduth zamani embodied.',
    threeQ: {
      is: 'Yes — this organism exists as a biological creation, an Ayah of Al-Bari, and a trust placed in this wetland.',
      what: 'Same genetic code, four body plans: egg, tadpole, froglet, adult. The transformation is written into the DNA from the beginning.',
      when: 'Egg stage: days. Tadpole: weeks. Metamorphosis: days. Adult: years. The Barzakh between tadpole and frog is the most dramatic transformation in vertebrate biology.',
    },
    names_detail: [
      { name: 'Al-Bari', ar: 'البارئ', why: 'Evolves the creature through stages it does not understand. Four body plans from one genetic alphabet.' },
      { name: 'Al-Muhawwil', ar: 'المحوّل', why: 'Gills dissolving, lungs inflating, tail reabsorbed. Radical transformation.' },
    ],
    sunnat: [
      'Egg in water',
      'Tadpole hatches with gills',
      'Hind legs emerge',
      'Front legs emerge',
      'Gills close, lungs inflate (the Barzakh)',
      'Tail reabsorbed (body consumes its own history)',
      'Froglet emerges onto land',
      'Adult completes maturation',
    ],
    zahir: 'A frog sitting on a lily pad',
    batin: 'A creature that has lived in two completely different worlds, breathed with two completely different organs, and moved with two completely different body plans — all from one genetic code.',
    haqq: 'Transformation is prescribed. The same essence can express radically different forms depending on huduth zamani.',
    amanah: 'Amphibians require both water AND land. Without clean water for eggs, the cycle breaks. Water quality IS metamorphosis preservation.',
    bridges: [
      { id: 's30', label: 'Weak Nuclear: Al-Muhawwil at subatomic level', type: 'process' },
    ],
  },
  {
    id: 's6', seq: 6, title: 'Salmon Run', loc: 'Portland', cat: 'Creation', biome: 'rainforest', bg: '#1a2a30', type: 'eco',
    ayah: 'And there is no creature on earth but upon Allah is its provision. (11:6)',
    names: ['hadi', 'razzaq', 'muid'],
    encounter: '3,000 miles through open ocean to the exact gravel bar of birth. The dying salmon IS the provision — marine nutrients feeding inland forests.',
    threeQ: {
      is: 'Yes — this salmon exists as a biological creation, an Ayah of Al-Hadi, and a trust placed in this watershed.',
      what: 'Pacific salmon navigate using magnetic field and olfactory memory. The map is written in the fish, not learned by it.',
      when: 'Ocean phase: 2-5 years. Return migration: weeks. Spawning: days. Death: immediate. The entire life is preparation for the return.',
    },
    names_detail: [
      { name: 'Al-Hadi', ar: 'الهادي', why: 'Navigates using magnetic field and olfactory memory. A 3,000-mile map in a creature that fits in your hand.' },
      { name: 'Al-Mu\'id', ar: 'المعيد', why: 'The return is decreed. Ocean to river to gravel to egg to ocean again.' },
    ],
    sunnat: [
      'Egg in gravel',
      'Fry imprints on stream chemistry',
      'Smolt transforms for ocean',
      'Ocean feeding 2-5 years',
      'Return migration triggered',
      'Upstream journey against current',
      'Arrival at birth gravel',
      'Spawning',
      'Death and decomposition (provision)',
      'Forest growth from marine nutrients',
    ],
    zahir: 'A fish swimming upstream',
    batin: 'Marine nutrients from the Pacific Ocean feeding trees 100 miles inland. The salmon is a nutrient pipeline connecting ocean to forest.',
    haqq: 'Return is decreed. Sacrifice IS provision. The most profound connections span thousands of miles.',
    amanah: 'Salmon runs declined 90%+. Dams block the return Al-Mu\'id decreed. The disruption of a divine provision system.',
    bridges: [
      { id: 's10', label: 'Monarch Migration (continental navigation parallel)', type: 'process' },
      { id: 's32', label: 'Flight: Ar-Rahman holds migrating birds', type: 'process' },
    ],
  },
  {
    id: 's7', seq: 7, title: 'Longleaf Pine Grass Stage', loc: 'Durham', cat: 'Creation', biome: 'piedmont_coast', bg: '#1a3a26', type: 'eco',
    ayah: 'Indeed, with hardship comes ease. (94:6)',
    names: ['sabur', 'batin', 'baqi'],
    encounter: '5-7 years as a tuft of needles. Not grass. A tree. Doing the most important work of its 500-year lifespan and it looks like nothing.',
    threeQ: {
      is: 'Yes — this seedling exists as a biological creation, an Ayah of As-Sabur, and a trust placed in this piedmont.',
      what: 'Pinus palustris. The grass stage is not failure — it is foundation-building. 90% of biomass underground.',
      when: 'Grass stage: 5-7 years. Bolt: 1 year. Maturity: 100 years. Full old-growth: 500 years. The patience is proportional to the lifespan.',
    },
    names_detail: [
      { name: 'As-Sabur', ar: 'الصبور', why: '5-7 years appearing to do nothing while building invisible foundations. What looks like failure is root-building.' },
      { name: 'Al-Batin', ar: 'الباطن', why: '90% of biomass underground. The visible is almost nothing. The hidden is everything.' },
    ],
    sunnat: [
      'Cone opens in fire',
      'Seed falls on burned soil',
      'Grass stage begins (As-Sabur)',
      'Fire passes over — seedling survives',
      'Bolt — explosive vertical growth',
      'Canopy emergence (Al-Ali)',
      'Maturity and cone production',
    ],
    zahir: 'A tuft of grass-like needles on the forest floor',
    batin: 'A 500-year tree in its foundation stage. The most important work of its life is invisible.',
    haqq: 'Foundation-building is invisible. Patience is not passive. What looks like failure may be the most important work happening.',
    amanah: 'Less than 3% of Longleaf savanna remains. Durham\'s communities in their grass stage are building foundations not yet visible.',
    bridges: [
      { id: 's13', label: 'The Waiting Seed (potential before manifestation)', type: 'name' },
    ],
  },
  {
    id: 's8', seq: 8, title: 'River Bluff Geology', loc: 'Cincinnati', cat: 'Process', biome: 'bluffs', bg: '#1a2630', type: 'eco',
    ayah: 'We have not neglected in the Register a thing. (6:38)',
    names: ['qawi', 'baqi', 'alim'],
    encounter: '450 million years of testimony in stone. Trilobites, bryozoans, brachiopods preserved in perfect detail.',
    threeQ: {
      is: 'Yes — this cliff exists as a geological formation, an Ayah of Al-Baqi, and a trust placed in this river valley.',
      what: 'Ordovician limestone. Each layer is a chapter. The industrial age written in lead. Pre-colonial era in diverse pollen.',
      when: 'Deposition: 450 million years ago. Burial: millions of years. Uplift: millions of years. Erosion: ongoing. Human reading: now.',
    },
    names_detail: [
      { name: 'Al-Baqi', ar: 'الباقي', why: '450 million years of testimony preserved. The form endures long after the organism vanished.' },
      { name: 'Ash-Shahid', ar: 'الشهيد', why: 'Each layer is testimony. The industrial age written in lead. Pre-colonial era in diverse pollen.' },
    ],
    sunnat: [
      'Organisms live and die',
      'Shells accumulate on sea floor',
      'Sediment buries remains',
      'Pressure lithifies to rock (Al-Qadir)',
      'Tectonic forces uplift (Ar-Rafi)',
      'River erosion exposes layers (Al-Qahhar)',
      'Human reads the exposed rock',
    ],
    zahir: 'A cliff face of layered rock above the river',
    batin: '450 million years of testimony. Every organism that ever lived in this sea left a record. The register neglects nothing.',
    haqq: 'Testimony endures beyond the testifier. The register neglects nothing. What we dismiss as inert stone is the most complete record available.',
    amanah: 'Cincinnati\'s fossil beds are world-class yet unrecognized. Children walk on 450 million years of testimony daily. The amanah is to teach them to read it.',
    bridges: [
      { id: 's25', label: 'Cliff Face: Al-Qahhar\'s Gallery', type: 'process' },
      { id: 's30', label: 'Weak Nuclear: the decay clock that dated these layers', type: 'process' },
    ],
  },
  {
    id: 's9', seq: 9, title: 'Three Rivers Confluence', loc: 'Pittsburgh', cat: 'Process', biome: 'confluence', bg: '#0e2a3a', type: 'eco',
    ayah: 'And He has subjected to you whatever is in the heavens and whatever is on the earth. (45:13)',
    names: ['jami', 'qadir', 'hakeem'],
    encounter: 'Allegheny and Monongahela merge to form the Ohio. Three separate watersheds merge chemistry, sediment, and species into one current.',
    threeQ: {
      is: 'Yes — this confluence exists as a geographical process, an Ayah of Al-Jami, and a trust placed in this watershed.',
      what: 'Two rivers of different chemistry, temperature, and sediment load merge. The Ohio carries both forward.',
      when: 'This confluence has existed for millions of years. The rivers have been gathering since the last ice age.',
    },
    names_detail: [
      { name: 'Al-Jami', ar: 'الجامع', why: 'Three become one. The gathering creates maximum biodiversity — the meeting point is the richest point.' },
      { name: 'Al-Hakeem', ar: 'الحكيم', why: 'The confluence creates an eddy — rest within the flow. The gathering point is inherently a place of refuge.' },
    ],
    sunnat: [
      'Headwater springs emerge',
      'Tributaries gather into rivers',
      'Two rivers approach from opposite directions',
      'Confluence occurs (Al-Jami)',
      'Eddy zone forms (Al-Hakeem)',
      'Combined flow continues as Ohio',
      '981 miles later joins Mississippi',
    ],
    zahir: 'Two rivers meeting at a point',
    batin: 'The richest ecological zone in any river system is the confluence. Gathering creates abundance.',
    haqq: 'Gathering is divinely designed. The meeting point of different streams is the richest point. What merges carries its testimony forward.',
    amanah: 'Coastal development disrupting tidal flow interrupts a mercy operating for 4.5 billion years.',
    bridges: [
      { id: 's22', label: 'Tidal Rhythm (celestial gathering of gravitational forces)', type: 'process' },
    ],
  },
  {
    id: 's10', seq: 10, title: 'Monarch Migration', loc: 'Chicago', cat: 'Creation', biome: 'prairie', bg: '#3a2a1a', type: 'eco',
    ayah: 'He knows its place of dwelling and its place of storage. (11:6)',
    names: ['hadi', 'qadir', 'alim', 'muhaymin'],
    encounter: '3,000 miles from Chicago to Mexico. Sun compass + magnetic sense in a brain with fewer neurons than a teaspoon of cortex. Fourth generation of a relay race no individual completes.',
    threeQ: {
      is: 'Yes — this butterfly exists as a biological creation, an Ayah of Al-Hadi, and a trust placed in this prairie.',
      what: 'Danaus plexippus. The migratory generation lives 8x longer than summer generations. The route is written in it, not learned by it.',
      when: 'Summer: 3 short-lived generations. Autumn: the migratory generation emerges. Winter: overwintering in Mexico. Spring: return begins.',
    },
    names_detail: [
      { name: 'Al-Hadi', ar: 'الهادي', why: 'A 3,000-mile map in 0.5 grams. The route is written in it, not learned by it.' },
      { name: 'Al-Muhaymin', ar: 'المهيمن', why: 'The migratory generation lives 8x longer — guarded specifically for the journey.' },
    ],
    sunnat: [
      'Overwintering monarchs emerge in Mexico',
      'Gen 1 flies north, lays eggs on milkweed, dies',
      'Caterpillar consumes milkweed, stores toxins',
      'Chrysalis — complete dissolution and reconstruction',
      'Gens 2-3 repeat northward',
      'Gen 4 enters reproductive diapause',
      '3,000-mile journey south (Al-Hadi)',
      'Arrival at specific oyamel fir grove (Al-Mu\'id)',
    ],
    zahir: 'An orange butterfly on a milkweed flower',
    batin: 'A 3,000-mile navigation system in 0.5 grams. A relay race across four generations where no individual sees the whole journey.',
    haqq: 'The most reliable pattern in creation is governed by relationships between bodies so distant you cannot see the cause. Mercy has a rhythm.',
    amanah: 'Monarch populations down 80%. Milkweed eliminated by herbicides. Planting milkweed in a community garden maintains a waypoint in a 3,000-mile provision network.',
    bridges: [
      { id: 's32', label: 'Flight: Ar-Rahman holds them', type: 'process' },
      { id: 's6', label: 'Salmon Run (continental navigation parallel)', type: 'process' },
    ],
  },
  {
    id: 's11', seq: 11, title: 'Sycamore Sentinel', loc: 'Louisville', cat: 'Creation', biome: 'bottomland', bg: '#1a3a2a', type: 'eco',
    ayah: 'And the earth — We spread it out and placed therein firmly set mountains. (50:7)',
    names: ['ali', 'khaliq', 'baqi', 'wadud'],
    encounter: '500 years on the same riverbank. Bark peels because the tree outgrows its own skin. Hollow trunks in elders become shelter — aging creates generosity.',
    threeQ: {
      is: 'Yes — this tree exists as a biological creation, an Ayah of Al-Khaliq, and a trust placed in this bottomland.',
      what: 'Platanus occidentalis. The largest deciduous tree in North America. Bark shedding is not disease — it is growth.',
      when: 'Seedling: rapid growth. Maturity: 100 years. Elder: 500 years. The hollow interior develops in the elder stage — generosity requires age.',
    },
    names_detail: [
      { name: 'Al-Khaliq', ar: 'الخالق', why: 'Created a tree whose growth requires visible shedding of its previous form. The biography written on its bark.' },
      { name: 'Al-Wadud', ar: 'الودود', why: 'The hollow interior becomes home for raccoons, owls, bats. The tree\'s loss IS others\' gain. Age creates capacity.' },
    ],
    sunnat: [
      'Flood deposits alluvium',
      'Seed germinates on bare soil',
      'Rapid growth (6ft/year)',
      'Bark develops, then bark shedding begins',
      'Root mass grips the bank (Al-Qawi)',
      'Heartwood decays — interior opens',
      'Hollow becomes shelter (Al-Wadud)',
      'Tree continues growing with hollow core',
    ],
    zahir: 'A massive white-barked tree on the riverbank',
    batin: 'A 500-year flood defense. A wildlife apartment building. A carbon storage system. A soil anchor. All from one tree.',
    haqq: 'Growth requires shedding. Aging creates capacity for generosity. The most persistent structures are divine, not human.',
    amanah: 'Riparian sycamores are the primary flood defense. Their removal accelerates flooding that damages communities in the floodplain.',
    bridges: [
      { id: 's21', label: 'Date Palm (long-lived provision tree parallel)', type: 'name' },
    ],
  },
  {
    id: 's12', seq: 12, title: 'Missouri River & Loess Hills', loc: 'Omaha', cat: 'Process', biome: 'plains', bg: '#1a2a30', type: 'eco',
    ayah: 'And the earth — We spread it and placed therein firmly set mountains. (15:19)',
    names: ['musawwir', 'qadir', 'qawi'],
    encounter: '200 feet of pure windblown silt. Particle size 0.002-0.05mm — the exact range wind can carry but water can\'t dissolve. The taqdir of grain size makes the entire landform possible.',
    threeQ: {
      is: 'Yes — these hills exist as a geological formation, an Ayah of Al-Musawwir, and a trust placed in this landscape.',
      what: 'Loess: wind-deposited silt. Particle size is the taqdir. A slight change in diameter eliminates the entire landform.',
      when: 'Deposition: 12,000-18,000 years ago. Stabilization by grass: 10,000 years ago. Ongoing: the grass holds what the wind would take.',
    },
    names_detail: [
      { name: 'Al-Musawwir', ar: 'المصوّر', why: 'Grain size as taqdir. A slight change in particle diameter eliminates the entire landform. The measurement makes the continent.' },
      { name: 'Al-Qawi', ar: 'القوي', why: 'Grass roots hold fragile loess against wind. Strength through flexibility. Remove the grass and the hills blow away.' },
    ],
    sunnat: [
      'Ice sheet grinds bedrock to silt (Al-Qahhar)',
      'Meltwater carries silt to valley',
      'Wind lifts particles (Al-Qadir)',
      'Silt deposits in lee of valley',
      'Accumulation over millennia (As-Sabur)',
      'Grass colonizes (Al-Muhyi)',
      'Root network stabilizes against wind',
      'Hills persist in dynamic equilibrium',
    ],
    zahir: 'Rolling hills of grass above the Missouri River',
    batin: '200 feet of pure windblown silt, held in place by grass roots. Remove the grass and the hills blow away in a generation.',
    haqq: 'The most precise measurements (grain size) determine the largest outcomes (landform existence). The force that creates can also destroy.',
    amanah: 'Loess Hills host the region\'s rarest species. Overgrazing exposes loess to the wind that built it. The most unique landscape is the most fragile.',
    bridges: [
      { id: 's1', label: 'Big Bluestem (the same grass stabilizes prairie and loess)', type: 'cycle' },
    ],
  },
  {
    id: 's13', seq: 13, title: 'The Waiting Seed', loc: 'All Cities', cat: 'Creation', biome: 'cross_biome', bg: '#1a2a1a', type: 'eco',
    ayah: 'He originates creation, then repeats it. (10:4)',
    names: ['badi', 'qadir', 'hafiz', 'muqaddim'],
    encounter: 'A seed in your palm. It weighs nothing. It IS a tree. A forest. A thousand years of provision. All of this and it appears to be nothing. The isness of decree held.',
    threeQ: {
      is: 'Yes — this seed exists as a biological creation, an Ayah of Al-Qadir, and a trust placed in every hand that holds it.',
      what: 'Compression ratio 1:16,000,000. A 3-gram acorn contains the blueprint for a 50,000kg oak. Power as encoding.',
      when: 'Dormancy: months to 1,300 years. Germination: days. Growth: centuries. The seed\'s time is not our time.',
    },
    names_detail: [
      { name: 'Al-Qadir', ar: 'القدرة', why: 'Compression ratio 1:16,000,000. A 3-gram acorn contains the blueprint for a 50,000kg oak. Power as encoding.' },
      { name: 'Al-Hafiz', ar: 'الحفيظ', why: 'Lotus seeds viable after 1,300 years. A library built to survive the end of the world.' },
    ],
    sunnat: [
      'Decree written (Al-Alim)',
      'Seed formed within fruit (Al-Musawwir)',
      'Seed dispersed (Al-Hadi)',
      'Dormancy (As-Sabur made molecular)',
      'Conditions align (Al-Muqaddim)',
      'Water penetrates shell (Al-Fattah)',
      'Enzymes activate — stored energy released (Ar-Razzaq)',
      'Radicle emerges downward (Al-Hadi via gravity)',
      'Plumule emerges upward (An-Nur)',
      'Photosynthesis begins — self-sustaining',
    ],
    zahir: 'A small seed in the palm of a hand',
    batin: 'Kun fayakun made physical. The greatest potential is held in the smallest vessel. The decree precedes manifestation by whatever duration Al-Muqaddim appoints.',
    haqq: 'Kun fayakun made physical. The greatest potential is held in the smallest vessel. The decree precedes manifestation by whatever duration Al-Muqaddim appoints.',
    amanah: 'Every seed in your ward is a decree waiting to execute. Community organizations in their first year are seeds. The amanah of potential itself.',
    bridges: [
      { id: 's7', label: 'Longleaf Grass Stage (7-year germination)', type: 'name' },
      { id: 's33', label: 'Quantum: superposition — the seed as macroscopic analogy', type: 'process' },
    ],
  },
  {
    id: 's14', seq: 14, title: 'The Torrent\'s Foam', loc: 'All Cities', cat: 'Process', biome: 'cross_biome', bg: '#1a1a2a', type: 'eco',
    ayah: 'Thus sets forth Allah Al-Haqq, The Truth, and the falsehood. Then as for the foam, it passes away. (13:17)',
    names: ['haqq', 'muqsit', 'khaliq'],
    encounter: 'Foam on a rushing stream. The most visible feature. The least substantial. Beneath it: dissolved minerals that will make the soil fertile. The foam passes. What benefits remains.',
    threeQ: {
      is: 'Yes — this foam exists as a physical phenomenon, an Ayah of Al-Haqq, and a teaching placed in every stream.',
      what: 'Foam: air bubbles trapped by surface tension. Water: dissolved minerals, nutrients, sediment. The foam is visible. The provision is invisible.',
      when: 'The foam lasts seconds. The minerals last millennia. The Quranic parable is timeless.',
    },
    names_detail: [
      { name: 'Al-Haqq', ar: 'الحق', why: 'The haqq is the water beneath — the carrier of provision. Falsehood is the foam — visible, prominent, passing away.' },
      { name: 'Al-Muqsit', ar: 'المقسط', why: 'Valleys flow according to their measure. Water distributed by decree, not chance.' },
    ],
    sunnat: [
      'Rain descends (Ar-Razzaq)',
      'Water gathers in valleys by measure (Al-Muqsit)',
      'Torrent carries foam on surface',
      'Foam obscures the water beneath',
      'Foam passes away as scum (Al-Haqq)',
      'Water deposits sediment and nutrients',
      'What benefits mankind remains (Al-Baqi)',
    ],
    zahir: 'White foam on a rushing stream',
    batin: 'The foam is the least substantial part. The provision — dissolved minerals, nutrients, sediment — is invisible beneath it.',
    haqq: 'Learn to see through the foam to the water. The foundational skill of the IRL Lens.',
    amanah: 'The MRG documents what the metrics miss. A food desert statistic is the water beneath the foam of economic development. The caretaker who sees through foam to water IS applying this parable.',
    bridges: [
      { id: 's2', label: 'Wetland Filtration (the marsh filters the torrent)', type: 'cycle' },
      { id: 's9', label: 'Three Rivers Confluence (three torrents merging)', type: 'cycle' },
    ],
  },
  {
    id: 's15', seq: 15, title: 'Thunder & Lightning', loc: 'All Cities', cat: 'Process', biome: 'cross_biome', bg: '#2a1a2a', type: 'eco',
    ayah: 'And glorifies the thunder with His praise. (13:13)',
    names: ['qadir', 'jabbar', 'razzaq'],
    encounter: '30,000 Kelvin. 0.0002 seconds. 300 million volts. The most violent energy transfer in the atmosphere. Also: glorification. And: 13,000 tons of nitrogen fertilizer delivered to Earth\'s soils annually.',
    threeQ: {
      is: 'Yes — lightning exists as a physical phenomenon, an Ayah of Al-Qadir, and a provision placed in every storm.',
      what: 'Electrical discharge between cloud and ground. Temperature: 30,000K — 5x hotter than the sun\'s surface. Duration: 0.0002 seconds.',
      when: 'Every storm. 100 lightning strikes per second globally. 13,000 tons of nitrogen fixed annually.',
    },
    names_detail: [
      { name: 'Al-Qadir', ar: 'القدرة', why: 'Power expressed as instantaneous discharge. 30,000K in 0.0002 seconds.' },
      { name: 'Ar-Razzaq', ar: 'الرزاق', why: 'Lightning fixes nitrogen. The violent flash becomes soil fertilizer. Provision through the most terrifying delivery system.' },
    ],
    sunnat: [
      'Solar heating drives evaporation',
      'Warm air rises, condenses, charge separates',
      'Potential reaches critical threshold',
      'Stepped leader extends downward (Al-Hadi)',
      'Return stroke at 1/3 speed of light',
      'Thunder — the tasbih',
      'Nitrogen fixation (Ar-Razzaq)',
      'Nitrate absorbed by plant roots',
    ],
    zahir: 'A lightning bolt striking the ground',
    batin: '13,000 tons of nitrogen fertilizer delivered to Earth\'s soils annually. The most violent event in the atmosphere is also a provision system.',
    haqq: 'Glorification is not always gentle. Provision arrives through channels that terrify. The thing you flee from is praising the One you seek.',
    amanah: 'Every storm delivers nitrogen to exposed soil — every garden, every vacant lot. Even the most violent disruptions carry benefit.',
    bridges: [
      { id: 's27', label: 'Electromagnetism (lightning is electromagnetic discharge)', type: 'process' },
      { id: 's3', label: 'Photosynthesis (nitrogen fixed by lightning feeds trees)', type: 'cycle' },
    ],
  },
  {
    id: 's16', seq: 16, title: 'Spider Silk', loc: 'All Forests', cat: 'Creation', biome: 'cross_biome', bg: '#1a2a1a', type: 'eco',
    ayah: 'He created the heavens and earth in truth and formed you and perfected your forms. (64:3)',
    names: ['musawwir', 'alim', 'hakeem', 'latif'],
    encounter: 'Stronger than steel by weight. More elastic than nylon. 380 million years old. 7 silk types from 7 glands. Human engineers cannot replicate it.',
    threeQ: {
      is: 'Yes — this silk exists as a biological material, an Ayah of Al-Musawwir, and a trust placed in every forest.',
      what: 'Protein fibers with crystalline and amorphous regions in unreplicable ratio. Stronger than steel, more elastic than nylon.',
      when: 'Spider silk has existed for 380 million years. Human materials science has been trying to replicate it for decades.',
    },
    names_detail: [
      { name: 'Al-Musawwir', ar: 'المصوّر', why: 'Molecular structure that human materials science cannot duplicate. Crystalline + amorphous regions in unreplicable ratio.' },
      { name: 'Al-Latif', ar: 'اللطيف', why: 'Nearly invisible. The web you destroyed in passing took 30 minutes to build and contains a breakthrough engineers covet.' },
    ],
    sunnat: [
      'Silk proteins produced in glands',
      'Liquid silk drawn through spinnerets (liquid→solid during extrusion)',
      'Radial threads laid first (frame)',
      'Capture spiral woven (function)',
      'Web tested (Al-Khabir)',
      'Spider waits (As-Sabur)',
      'Prey contacts web — vibrations transmitted',
      'Old web consumed, recycled (Al-Mu\'id)',
      'New web by morning',
    ],
    zahir: 'A spider web glistening with dew',
    batin: 'A material stronger than steel, more elastic than nylon, produced by a creature weighing less than a gram, that human civilization cannot replicate.',
    haqq: 'The subtlest materials can be the strongest. A creature weighing less than a gram produces what human civilization cannot replicate.',
    amanah: 'The spider rebuilds nightly. Resilience is not absence of destruction but capacity to recycle and rebuild. The means of production are within, not in the web.',
    bridges: [
      { id: 's4', label: 'Mycorrhizal Network (invisible network parallel)', type: 'name' },
    ],
  },
  {
    id: 's17', seq: 17, title: 'Duckweed', loc: 'Detroit', cat: 'Creation', biome: 'wetland', bg: '#0e3a2e', type: 'eco',
    ayah: 'And of His signs is the creation of the heavens and the earth and the diversity of your languages and your colors. (30:22)',
    names: ['latif', 'muhyi', 'razzaq'],
    encounter: '1-3mm across. The smallest flowering plant. Doubles every 48 hours. 40% protein — more per acre than soybeans. The green scum is a protein factory.',
    threeQ: {
      is: 'Yes — this plant exists as a biological creation, an Ayah of Al-Latif, and a trust placed in every still water.',
      what: 'Lemna minor. 1-3mm. No roots. No stem. No leaves. Just a frond. The simplest flowering plant.',
      when: 'Single frond: 48 hours to double. Colony: days. Surface coverage: weeks. The smallest plant has the fastest growth rate.',
    },
    names_detail: [
      { name: 'Al-Latif', ar: 'اللطيف', why: 'The smallest flowering plant. Divine economy inverts scale assumptions. The smallest creation may be the richest provision.' },
      { name: 'Ar-Razzaq', ar: 'الرزاق', why: '40% protein, grows without soil, purifies the water it grows on. Provision placed where least expected.' },
    ],
    sunnat: [
      'Single frond floating',
      'Budding — daughter frond emerges',
      'Daughter detaches (48 hours)',
      'Colony forms',
      'Surface mat provides shade, reduces evaporation',
      'Flowering (smallest flower in the world)',
      'Turions sink for winter (zahir→batin)',
      'Spring: turions rise (Al-Mu\'id)',
    ],
    zahir: 'Green dots floating on still water',
    batin: 'The world\'s fastest-growing crop. 40% protein. Grows without soil. Purifies the water it grows on. The green scum is a food security solution.',
    haqq: 'The smallest creation may contain the richest provision. What we dismiss as scum may solve the food security crisis.',
    amanah: 'Every still water surface in Detroit is a potential protein farm. Duckweed cultivation addresses food insecurity AND water quality simultaneously.',
    bridges: [
      { id: 's24', label: 'Desert Succulent (smallest organism, largest provision parallel)', type: 'name' },
    ],
  },
  {
    id: 's18', seq: 18, title: 'Clay', loc: 'All Cities', cat: 'Process', biome: 'cross_biome', bg: '#2a2016', type: 'eco',
    ayah: 'We created man from sounding clay, from mud molded into shape. (15:26)',
    names: ['musawwir', 'hayy', 'rahman'],
    encounter: 'The material of Adam\'s creation is the material that holds the nutrient bank of the biosphere. Particles <0.002mm carrying electrical charge. Without clay, nutrients wash to the ocean.',
    threeQ: {
      is: 'Yes — this clay exists as a geological material, an Ayah of Al-Musawwir, and a trust placed in every soil.',
      what: 'Particles <0.002mm. Electrical charge. Cation exchange capacity. The nutrient bank of the biosphere.',
      when: 'Clay formation: millions of years. Soil accumulation: thousands of years. Nutrient exchange: continuous.',
    },
    names_detail: [
      { name: 'Al-Musawwir', ar: 'المصوّر', why: 'The taqdir of plasticity — the capacity to receive and hold a form. The material was made for the fashioning.' },
      { name: 'Ar-Razzaq', ar: 'الرزاق', why: 'Clay\'s electrical charge holds nutrients against gravity. The provision system of the entire terrestrial biosphere.' },
    ],
    sunnat: [
      'Parent rock weathers',
      'Physical weathering breaks rock',
      'Chemical weathering transforms minerals',
      'Clay minerals form (Al-Musawwir)',
      'Clay accumulates in soil horizons',
      'Nutrient ions adsorb to surfaces (Ar-Razzaq)',
      'Plant roots exchange at clay surfaces',
      'Organo-mineral complexes form (Al-Baqi)',
      'Fired clay becomes pottery (mata)',
    ],
    zahir: 'Wet clay in the hands',
    batin: 'The material of Adam\'s creation is the material of our sustenance. The smallest particles carry the greatest responsibility.',
    haqq: 'The material of our origin is the material of our sustenance. The smallest particles carry the greatest responsibility.',
    amanah: 'Water contamination in every city degrades the most precisely fashioned substance in creation. Every water quality program is Islah at the molecular level.',
    bridges: [
      { id: 's29', label: 'Thermodynamics (clay formation is a thermodynamic process)', type: 'process' },
    ],
  },
  {
    id: 's19', seq: 19, title: 'Savannah Acacia', loc: 'Savannah', cat: 'Creation', biome: 'savannah', bg: '#3a3a1a', type: 'eco',
    ayah: 'And the earth — We have spread it and placed therein firmly set mountains. (50:7)',
    names: ['rahman', 'muhaymin', 'ghani', 'khabir'],
    encounter: 'Flat-topped, thorned, the only shade for hundreds of meters. Animals gather. Seeds germinate. Life concentrates. The tree that provides mercy is covered in thorns.',
    threeQ: {
      is: 'Yes — this tree exists as a biological creation, an Ayah of Ar-Rahman, and a trust placed in this savannah.',
      what: 'Acacia tortilis. Flat crown: mercy architecture. Thorns: protection. Nitrogen fixation: self-sufficiency. Chemical alarm: awareness.',
      when: 'Seedling: rapid taproot descent. Juvenile: thorns develop immediately. Mature: flat crown spreads. Elder: hollow thorns host ant colonies.',
    },
    names_detail: [
      { name: 'Ar-Rahman', ar: 'الرحمن', why: 'The flat crown IS mercy architecture — spreading outward to cover rather than reaching upward to escape. Shade in lethal heat.' },
      { name: 'Al-Khabir', ar: 'الخبير', why: 'Volatile chemical signals between acacias. Awareness transmitted through air without consciousness.' },
    ],
    sunnat: [
      'Seed germinates in predecessor\'s nutrient legacy',
      'Juvenile develops thorns immediately',
      'Taproot descends to groundwater (Al-Batin)',
      'Nitrogen fixation begins (Al-Ghani)',
      'Flat crown develops — horizontal, not vertical',
      'Shade zone establishes (Ar-Rahman)',
      'Ant colony in hollow thorns (Al-Wadud)',
      'Chemical alarm network activates (Al-Khabir)',
    ],
    zahir: 'A flat-topped tree on the open savannah',
    batin: 'A nitrogen factory, a chemical communication network, a wildlife apartment building, a shade provider — all in one tree.',
    haqq: 'Provision and protection from the same branches. Enrichment radiates outward. Mercy\'s gesture is horizontal — covering, not climbing.',
    amanah: 'Acacia clearing removes shade zones, nitrogen sources, communication nodes. Agroforestry that integrates acacias is Islah preserving function.',
    bridges: [
      { id: 's4', label: 'Mycorrhizal Network (underground cooperation)', type: 'name' },
    ],
  },
  {
    id: 's20', seq: 20, title: 'Mangrove Barzakh', loc: 'Coastal', cat: 'Process', biome: 'coastal', bg: '#0a2a2a', type: 'eco',
    ayah: 'He released the two seas, meeting together. Between them is a barrier they do not transgress. (55:19-20)',
    names: ['quddus', 'muhaymin', 'hayy', 'hafiz'],
    encounter: 'Trees growing in seawater. The physical ayah of Surah Ar-Rahman\'s Barzakh. 80% of tropical fisheries nursery. Carbon sequestration 3-5x terrestrial forests.',
    threeQ: {
      is: 'Yes — this mangrove exists as a biological creation, an Ayah of Al-Quddus, and a trust placed in this coastline.',
      what: 'Root membrane excludes 90% of salt. Pneumatophores breathe in anaerobic mud. Viviparous propagules germinate before dropping.',
      when: 'Propagule: already germinated when it drops. Establishment: weeks. Canopy closure: years. Carbon sequestration: centuries.',
    },
    names_detail: [
      { name: 'Al-Quddus', ar: 'القدوس', why: 'Root membrane excludes 90% of salt. Purification at the cellular level. The tree drinks seawater and lives.' },
      { name: 'Al-Muhaymin', ar: 'المهيمن', why: 'Guards coast (wave absorption), nursery (root shelter), and offspring (viviparous propagules) simultaneously.' },
    ],
    sunnat: [
      'Propagule drops at high tide (already germinated)',
      'Floats until suitable mud',
      'Root establishes in anaerobic mud',
      'Pneumatophores develop (breathing tubes)',
      'Prop roots extend',
      'Salt filtration activates (Al-Quddus)',
      'Canopy closes — habitat forms',
      'Nursery function — 80% of fisheries',
      'Carbon sequestered in anaerobic mud',
      'Storm arrives — roots absorb 66% of wave energy',
    ],
    zahir: 'Trees with tangled roots standing in water',
    batin: 'The physical Barzakhakh Surah Ar-Rahman. The boundary between salt and fresh, sea and land, is the most productive zone in the ocean.',
    haqq: 'The boundary between states is the most productive zone. Purification possible even in toxic medium. The Quranic Barzakh is ecological, observable, standing in the tide right now.',
    amanah: 'Mangroves reduced 35% since 1980. Destruction of the Barzakh dismantles a Quranic sign. Restoration is restoring the barrier Allah placed between two seas.',
    bridges: [
      { id: 's2', label: 'Wetland Filtration (saltwater purification parallel)', type: 'process' },
      { id: 's22', label: 'Tidal Rhythm (the rhythm the mangrove lives within)', type: 'cycle' },
    ],
  },
  {
    id: 's21', seq: 21, title: 'Date Palm', loc: 'Desert', cat: 'Creation', biome: 'desert', bg: '#3a2a1a', type: 'eco',
    ayah: 'And shake toward you the trunk of the palm tree; it will drop upon you ripe, fresh dates. (19:25)',
    names: ['razzaq', 'shakur', 'wadud', 'hakeem'],
    encounter: '600+ varieties. 140kg fruit/year in a landscape where nothing else fruit-bearing survives. Mentioned in the Quran more than any other tree. The tree that appeared to Maryam (AS) at the moment of greatest vulnerability.',
    threeQ: {
      is: 'Yes — this tree exists as a biological creation, an Ayah of Ar-Razzaq, and a trust placed in this desert.',
      what: 'Phoenix dactylifera. 600+ varieties. 30-meter taproot. 140kg fruit/year. No dormancy — provision is continuous.',
      when: 'Seedling: 4-8 years to first fruit. Productive: 100 years. Elder: 200+ years. No dormancy — provision is continuous.',
    },
    names_detail: [
      { name: 'Ar-Razzaq', ar: 'الرزاق', why: 'Provision where provision should be impossible. 140kg of sugar-rich fruit from a desert.' },
      { name: 'Al-Wadud', ar: 'الودود', why: 'The Quran directs the tree to provide to Maryam during childbirth. Provision at the moment of greatest need.' },
    ],
    sunnat: [
      'Seed germinates in oasis soil',
      'Taproot descends 30 meters (Al-Batin)',
      'Single trunk rises',
      'Fronds emerge (shed sand, deflect wind, channel dew)',
      'Flowering + pollination (human partnership IS part of the sunnat)',
      'Fruit develops through 5 stages (Arabic names for each)',
      'Harvest: 70-140kg/tree',
      'No dormancy — provision is continuous',
    ],
    zahir: 'A tall palm tree heavy with dates',
    batin: 'A 30-meter taproot reaching groundwater. 600+ varieties representing 6,000 years of human-divine partnership. No dormancy — provision is continuous.',
    haqq: 'Provision flourishes where scarcity is most extreme. The most generous outputs from minimal inputs. The human-tree partnership IS part of the design.',
    amanah: '600+ varieties represent 6,000 years of partnership with Al-Musawwir\'s fashioning. Each variety lost is a taqdir erased.',
    bridges: [
      { id: 's24', label: 'Desert Succulent (arid adaptation parallel)', type: 'process' },
    ],
  },
  {
    id: 's22', seq: 22, title: 'Tidal Rhythm', loc: 'Coastal', cat: 'Process', biome: 'coastal', bg: '#0a1a3a', type: 'eco',
    ayah: 'And the sun and the moon by precise calculation. (55:5)',
    names: ['muqaddim', 'qayyum', 'rahman'],
    encounter: '4.5 billion years. Twice daily. Governed by Earth-Moon-Sun gravitational relationship. More reliable than sunrise. Introduced in the Quran under the Name Ar-Rahman.',
    threeQ: {
      is: 'Yes — this tide exists as a physical phenomenon, an Ayah of Al-Muqaddim, and a trust placed in every coastline.',
      what: 'Differential gravity between Earth, Moon, and Sun creates two tidal bulges. Earth rotates beneath them.',
      when: 'Every 12.4 hours. For 4.5 billion years. Calculable centuries in advance. No human system matches this precision.',
    },
    names_detail: [
      { name: 'Al-Muqaddim', ar: 'المقدّم', why: 'Punctuality at cosmic scale. Tide tables calculable centuries in advance. No human system matches this precision over 4.5 billion years.' },
      { name: 'Ar-Rahman', ar: 'الرحمن', why: 'The tidal rhythm appears in Surah Ar-Rahman. The rhythm of advance and retreat IS mercy. Mercy has a schedule.' },
    ],
    sunnat: [
      'Moon orbits Earth',
      'Differential gravity creates two tidal bulges',
      'Earth rotates beneath the bulge',
      'High tide: water advances, marine nutrients reach shore',
      'Low tide: water retreats, shore organisms exposed',
      'Tidal currents mix nutrients from depth',
      'Intertidal organisms adapted to both states',
      'The cycle repeats. 4.5 billion years. bi husban.',
    ],
    zahir: 'Waves advancing and retreating on a shore',
    batin: 'A mercy operating for 4.5 billion years, governed by the gravitational relationship between three celestial bodies, calculable centuries in advance.',
    haqq: 'The most reliable pattern in creation is governed by relationships between bodies so distant you cannot see the cause. Mercy has a rhythm.',
    amanah: 'Coastal development disrupting tidal flow interrupts a mercy operating for 4.5 billion years.',
    bridges: [
      { id: 's26', label: 'Gravity (the force that drives tides)', type: 'process' },
    ],
  },
  {
    id: 's23', seq: 23, title: 'Coral Reef', loc: 'Ocean', cat: 'Creation', biome: 'ocean', bg: '#0a1a2a', type: 'eco',
    ayah: 'From both of them emerges pearl and coral. (55:22)',
    names: ['khaliq', 'wadud', 'sabur'],
    encounter: 'A city built by organisms the size of a pencil eraser. 1mm/year. 10,000 patient years = 10-meter reef visible from space. 25% of marine species on 0.1% of the ocean floor.',
    threeQ: {
      is: 'Yes — this reef exists as a biological creation, an Ayah of Al-Khaliq, and a trust placed in this ocean.',
      what: 'Coral polyp: 1-3mm. Calcium carbonate skeleton. Zooxanthellae living inside. 1mm/year growth.',
      when: '1mm/year. 10,000 years = 10-meter reef. The Great Barrier Reef: 500,000 years. Patience compounded is geology.',
    },
    names_detail: [
      { name: 'Al-Khaliq', ar: 'الخالق', why: 'An eraser-sized animal building geological formations visible from space. Creation through multiplication of the humble.' },
      { name: 'Al-Wadud', ar: 'الودود', why: 'Zooxanthellae live INSIDE the coral, feeding it from within. The most intimate symbiosis in creation. A 1-2°C rise breaks it.' },
    ],
    sunnat: [
      'Larva settles on hard substrate',
      'Metamorphoses into polyp',
      'Zooxanthellae acquired (Al-Jami)',
      'Photosynthesis inside the animal (Al-Wadud)',
      'Calcium carbonate secretion (Al-Musawwir)',
      'Colony grows upward at 1mm/year (As-Sabur)',
      'Reef framework becomes habitat',
      'Wave energy absorbed — coast protected',
      'Polyp dies — skeleton remains as foundation (Al-Mu\'id)',
    ],
    zahir: 'Colorful coral formations in clear water',
    batin: 'The most significant structures are built by the smallest faithful actors across incomprehensible time. Patience compounded is geology.',
    haqq: 'The most significant structures are built by the smallest faithful actors across incomprehensible time. Patience compounded is geology. The most magnificent is also the most fragile.',
    amanah: '50%+ of coral killed by ocean warming in 30 years. Every community\'s carbon footprint contributes. The amanah connects Chicago to an ocean it may never visit.',
    bridges: [
      { id: 's28', label: 'Strong Nuclear (calcium atoms in coral were forged by this force)', type: 'process' },
    ],
  },
  {
    id: 's24', seq: 24, title: 'Desert Succulent', loc: 'Desert', cat: 'Creation', biome: 'desert', bg: '#2a2a0a', type: 'eco',
    ayah: 'And you see the earth barren, but when We send down upon it rain, it quivers and swells. (22:5)',
    names: ['hayy', 'hakeem', 'muhaymin'],
    encounter: 'The organism that became its own water reservoir. CAM photosynthesis — breathing at night, photosynthesizing during the day behind sealed stomata. Survives 80% water loss.',
    threeQ: {
      is: 'Yes — this succulent exists as a biological creation, an Ayah of Al-Hayy, and a trust placed in this desert.',
      what: 'CAM photosynthesis: stomata open at night (cool, humid) to capture CO₂. Stomata sealed during day. Photosynthesis on stored CO₂.',
      when: 'Rain falls (rare). Rapid absorption. Inflation. Months of slow dehydration. Dormancy. Rain returns — rehydration in hours.',
    },
    names_detail: [
      { name: 'Al-Hayy', ar: 'الحي', why: 'Life refusing to end. Metabolizing at absolute minimum to persist through conditions hostile to all other life.' },
      { name: 'Al-Hakeem', ar: 'الحكيم', why: 'CAM photosynthesis inverts the schedule — same process, temporally rearranged for water conservation.' },
    ],
    sunnat: [
      'Rain falls (rare event)',
      'Shallow roots absorb rapidly',
      'Water stored in vacuoles — plant inflates',
      'Stomata close (Al-Muhaymin)',
      'Night: stomata open briefly, CO₂ captured',
      'Day: photosynthesis on stored CO₂, stomata sealed',
      'Slow dehydration over months (As-Sabur)',
      'Dormancy (Al-Hayy at minimum)',
      'Rain returns — rehydration in hours (Al-Muhyi)',
    ],
    zahir: 'A plump cactus in dry desert soil',
    batin: 'An organism that inverted the photosynthesis schedule, became its own water reservoir, and survives 80% water loss. The body can become the provision.',
    haqq: 'The body can become the provision. Wisdom means inverting the conventional process when the context demands it.',
    amanah: 'Every community facing scarcity already practices the succulent\'s sunnat — acquire rapidly, store efficiently, spend slowly, endure when the store runs out.',
    bridges: [
      { id: 's3', label: 'Photosynthesis: C3/C4/CAM — same command, three schedules', type: 'process' },
    ],
  },
  {
    id: 's25', seq: 25, title: 'The Cliff Face', loc: 'Geological', cat: 'Process', biome: 'geological', bg: '#2a2630', type: 'eco',
    ayah: 'And you see the mountains, thinking them rigid, while they will pass as the passing of clouds. (27:88)',
    names: ['qahhar', 'rafi', 'alim'],
    encounter: 'Every cliff face is evidence of Al-Qahhar. The surface subdued to reveal the geological record. Mountains ARE clouds in geological time.',
    threeQ: {
      is: 'Yes — this cliff exists as a geological formation, an Ayah of Al-Qahhar, and a trust placed in this landscape.',
      what: 'Erosion as revelation. Without Al-Qahhar removing the covering, the testimony stays buried.',
      when: 'Sediment accumulation: millions of years. Uplift: millions of years. Erosion: ongoing. New pages exposed continuously.',
    },
    names_detail: [
      { name: 'Al-Qahhar', ar: 'القهّار', why: 'The subduing IS the revelation. Without erosion, the testimony stays buried. Al-Qahhar curates by removing the covering.' },
      { name: 'Ar-Rafi', ar: 'الرافع', why: 'Tectonic uplift exalted ocean floor to mountaintop. Then Al-Qahhar subdued the surface to expose the record.' },
    ],
    sunnat: [
      'Sediment accumulates (writing)',
      'Burial and lithification (Al-Qadir)',
      'Tectonic uplift (Ar-Rafi)',
      'Erosion begins (Al-Qahhar)',
      'Differential erosion sculpts (Al-Musawwir through subtraction)',
      'Cliff face readable (Al-Alim\'s knowledge accessible)',
      'Erosion continues — new pages exposed',
    ],
    zahir: 'A cliff face of layered rock',
    batin: 'Revelation requires removal. The hidden becomes apparent only when the covering is subdued.',
    haqq: 'Revelation requires removal. The hidden becomes apparent only when the covering is subdued.',
    amanah: 'Every cliff, road cut, and outcrop is a library. Geological literacy is ecological literacy is community literacy.',
    bridges: [
      { id: 's8', label: 'River Bluff Geology (specific case in Ohio Valley)', type: 'process' },
    ],
  },
  {
    id: 's26', seq: 26, title: 'Gravity', loc: 'Physics', cat: 'Process', biome: 'physics', bg: '#1a1a2a', type: 'eco',
    ayah: 'And He has subjected to you whatever is in the heavens and whatever is on the earth. (45:13)',
    names: ['jabbar', 'qayyum', 'latif'],
    encounter: 'Drop anything. The most universal force. 10³⁶x weaker than electromagnetism yet it shapes galaxies. Always attractive. Never repulsive. Since 10⁻⁴³ seconds after creation.',
    threeQ: {
      is: 'Yes — gravity exists as a physical force, an Ayah of Al-Jabbar, and a trust placed in every particle.',
      what: 'Spacetime curvature. Every mass curves spacetime. Objects follow curved paths. The weakest force governs the largest structures.',
      when: 'Since 10⁻⁴³ seconds after creation. Before atoms. Before light. The first ordered sunnah.',
    },
    names_detail: [
      { name: 'Al-Jabbar', ar: 'الجبّار', why: 'Compels every particle toward every other particle. No exceptions. No appeals. But the gentlest force — compulsion through universality, not intensity.' },
      { name: 'Al-Latif', ar: 'اللطيف', why: 'The weakest force governing the largest structures. The subtlest influence achieves the largest outcomes.' },
    ],
    sunnat: [
      'Mass curves spacetime',
      'Objects follow curved paths (orbits)',
      'Gravity retains atmosphere',
      'Drives the water cycle (rain falls, rivers flow)',
      'Causes tides (lunar gravity)',
      'Seeds fall to ground',
      'Roots grow downward (gravitropism)',
      'Trees grow upward AGAINST gravity (Al-Ali)',
    ],
    zahir: 'An apple falling from a tree',
    batin: 'The most universal decree is the most subtle. Compulsion through gentleness. The weakest force wins by never stopping and never repelling. Every fall is a return.',
    haqq: 'The most universal decree is the most subtle. Compulsion through gentleness. The weakest force wins by never stopping and never repelling. Every fall is a return.',
    amanah: 'Systems that work WITH gravity (terraced agriculture, gravity-fed irrigation) align with the sunnat. Systems that fight gravity without necessity work against the first ordered sunnah.',
    bridges: [
      { id: 's22', label: 'Tidal Rhythm (gravity between Earth and Moon)', type: 'process' },
    ],
  },
  {
    id: 's27', seq: 27, title: 'Electromagnetism', loc: 'Physics', cat: 'Process', biome: 'physics', bg: '#1a1a3a', type: 'eco',
    ayah: 'Allah is the Light of the heavens and the earth. (24:35)',
    names: ['nur', 'matin', 'khabir'],
    encounter: 'The force responsible for ALL light, ALL chemistry, ALL nerve signals, ALL structural integrity of matter. The photon IS the force carrier. An-Nur IS the electromagnetic field.',
    threeQ: {
      is: 'Yes — electromagnetism exists as a physical force, an Ayah of An-Nur, and a trust placed in every photon.',
      what: 'The photon is the force carrier. Light, chemistry, nerve signals, structural integrity — all electromagnetic.',
      when: 'Since 10⁻³⁶ seconds after creation. The electromagnetic force separated from the unified force before atoms existed.',
    },
    names_detail: [
      { name: 'An-Nur', ar: 'النور', why: 'The photon — the particle of light — is the carrier of the force that makes chemistry, color, and consciousness possible.' },
      { name: 'Al-Matin', ar: 'المتين', why: 'Electromagnetism is what makes matter solid. Without it, every object disintegrates.' },
    ],
    sunnat: [
      'Photon emitted from solar fusion',
      'Travels 93 million miles at exactly 299,792,458 m/s',
      'Enters atmosphere (blue scatters, red sunsets)',
      'Strikes chlorophyll (Sequence 3)',
      'Electron absorbs photon, jumps energy level',
      'Electron transport chain',
      'ATP synthesized',
      'Glucose produced',
      'You eat the fruit — the photon\'s energy released in your cells',
    ],
    zahir: 'Light streaming through a window',
    batin: 'An-Nur is not metaphorical. The visible band is 0.0035% of the electromagnetic spectrum. The green of leaves is the REJECTED wavelength. The actual provision is invisible.',
    haqq: 'An-Nur is not metaphorical. The visible band is 0.0035% of the electromagnetic spectrum. The green of leaves is the REJECTED wavelength. The actual provision is invisible.',
    amanah: 'Light pollution, electromagnetic interference, spectral poverty of artificial lighting — all disrupt An-Nur\'s provision system.',
    bridges: [
      { id: 's3', label: 'Photosynthesis (electromagnetism in every step)', type: 'process' },
      { id: 's15', label: 'Lightning (electromagnetic discharge)', type: 'process' },
    ],
  },
  {
    id: 's28', seq: 28, title: 'Strong Nuclear Force', loc: 'Physics', cat: 'Process', biome: 'physics', bg: '#1a0a1a', type: 'eco',
    ayah: 'And We sent down the Iron, wherein power mighty and benefits. (57:25)',
    names: ['matin', 'samad', 'khaliq'],
    encounter: '100x stronger than electromagnetism. Range: 10⁻¹⁵m. Holds every nucleus against electromagnetic repulsion. Without it: no atoms beyond hydrogen. No carbon. No you. Attempting to separate quarks creates MORE quarks.',
    threeQ: {
      is: 'Yes — the strong nuclear force exists as a physical force, an Ayah of Al-Matin, and a trust placed in every nucleus.',
      what: 'Gluon exchange between quarks. Residual strong force between nucleons. The binding that cannot be broken.',
      when: 'Since 10⁻³⁶ seconds after creation. Before atoms. The force that made chemistry possible.',
    },
    names_detail: [
      { name: 'Al-Matin', ar: 'المتين', why: 'The binding that cannot be broken. Attempting to divide what Al-Matin has bound creates more unity. Tawhid enforced at the subatomic level.' },
      { name: 'Al-Samad', ar: 'الصمد', why: 'The nucleus requires no external input to maintain its structure. Self-sufficient for the lifetime of the universe.' },
    ],
    sunnat: [
      'Quarks bind into protons/neutrons (gluon exchange)',
      'Nucleons bind into nuclei (residual strong force)',
      'Hydrogen fuses in stellar cores',
      'Heavier elements forged in succession',
      'Elements beyond iron forged in supernovae',
      'Elements scatter, gather into new systems (Al-Jami)',
      'Your body\'s atoms held by the same force that held them in a star',
    ],
    zahir: 'The solid objects around you',
    batin: 'The most powerful force operates at the smallest invisible scale. Attempting to divide what Al-Matin has bound creates more unity. Tawhid in physics.',
    haqq: 'The most powerful force operates at the smallest invisible scale. Attempting to divide what Al-Matin has bound creates more unity. Tawhid in physics.',
    amanah: 'The firmness of everything you touch, build, and inhabit depends on a force you will never perceive. The substrate of your existence is the most hidden ayah.',
    bridges: [
      { id: 's26', label: 'Gravity (bookend — weakest but most universal)', type: 'process' },
    ],
  },
  {
    id: 's29', seq: 29, title: 'Thermodynamics', loc: 'Physics', cat: 'Process', biome: 'physics', bg: '#1a1a1a', type: 'eco',
    ayah: 'And the heaven He raised it and He has set up the Mizan — The Balance. (55:7)',
    names: ['muqit', 'hakeem', 'muhyi', 'rahman'],
    encounter: 'Energy conserved (First Law = Al-Muqit). Entropy increases (Second Law = arrow of time). Life locally reverses entropy — purchased by An-Nur\'s energy. The Mizan of Surah Ar-Rahman IS the thermodynamic balance.',
    threeQ: {
      is: 'Yes — thermodynamics exists as a physical law, an Ayah of Al-Muqit, and a trust placed in every energy exchange.',
      what: 'First Law: energy conserved. Second Law: entropy increases. Life: local reversal of entropy purchased by continuous mercy.',
      when: 'Since the first moment. The Second Law is the arrow of time. The First Law has held since Kun.',
    },
    names_detail: [
      { name: 'Al-Muqit', ar: 'المقيت', why: 'The First Law. Not an electron-volt unaccounted for since Kun. Energy sustained through transformation, not addition.' },
      { name: 'Ar-Rahman', ar: 'الرحمن', why: 'The Mizan of Surah Ar-Rahman IS the thermodynamic balance. Do not make deficient the Mizan — work within the balance.' },
    ],
    sunnat: [
      'Photon arrives at leaf (electromagnetic energy)',
      'Chloroplast converts to chemical energy (First Law: same energy, different form)',
      'Some energy lost as heat at each step (Second Law: entropy tax)',
      'Tree respires — releases energy for growth',
      'Animal eats fruit, releases energy',
      'Death and decomposition (Second Law reasserts)',
      'Energy dispersed but not gone (First Law holds)',
    ],
    zahir: 'Heat rising from a warm surface',
    batin: 'Energy is conserved with exactitude mirroring the Mizan. Life is a local reversal of entropy purchased by continuous mercy. Death is the Second Law reasserting.',
    haqq: 'Energy is conserved with exactitude mirroring the Mizan. Life is a local reversal of entropy purchased by continuous mercy. Death is the Second Law reasserting.',
    amanah: 'Community wellness IS an energy balance — provision in, maintenance sustained, entropy managed. When provision < entropy, the community degrades. This is the Second Law. The Mizan transgressed.',
    bridges: [
      { id: 's3', label: 'Photosynthesis (entropy reversal purchased by An-Nur)', type: 'process' },
    ],
  },
  {
    id: 's30', seq: 30, title: 'Weak Nuclear Force', loc: 'Physics', cat: 'Process', biome: 'physics', bg: '#1a0a2a', type: 'eco',
    ayah: 'He transforms the night into day and transforms the day into night. (57:6)',
    names: ['muhawwil', 'alim', 'mumit'],
    encounter: 'The transformer. Changes one element into another. The only force that changes particle identity. The clock of geological time — half-lives are the calendar of creation\'s history.',
    threeQ: {
      is: 'Yes — the weak nuclear force exists as a physical force, an Ayah of Al-Muhawwil, and a trust placed in every unstable nucleus.',
      what: 'W boson mediates. Quark flavor changes. Neutron becomes proton. Identity change at the most fundamental level.',
      when: 'Carbon-14: 5,730±40 years. Uranium-238: 4.5 billion years. The rate IS the clock. Al-Alim set the rate.',
    },
    names_detail: [
      { name: 'Al-Muhawwil', ar: 'المحوّل', why: 'Identity change at the most fundamental level. A neutron IS NOT a proton. Yet the weak force converts one into the other.' },
      { name: 'Al-Alim', ar: 'العليم', why: 'Half-life precision IS taqdir. Carbon-14: 5,730±40 years. The rate IS the clock. Al-Alim set the rate.' },
    ],
    sunnat: [
      'Unstable nucleus exists (designed instability)',
      'W boson mediates (10⁻²⁵ seconds)',
      'Quark flavor changes (identity shift)',
      'Nucleon transforms',
      'Electron and neutrino emitted',
      'Daughter element exists',
      'Process repeats at half-life rate',
    ],
    zahir: 'A Geiger counter clicking near a rock',
    batin: 'Transformation is fundamental — written into the deepest substrate. The clock by which we read creation\'s history IS the transformation process.',
    haqq: 'Transformation is fundamental — written into the deepest substrate. The clock by which we read creation\'s history IS the transformation process.',
    amanah: 'Nuclear waste concentrates weak-force transformations designed for billions of years into hazardous material lasting millennia. Communities near contaminated sites bear the burden of accelerated transformation.',
    bridges: [
      { id: 's8', label: 'River Bluff Geology (dated by this clock)', type: 'process' },
      { id: 's5', label: 'Amphibian Metamorphosis (transformation of identity at biological level)', type: 'name' },
    ],
  },
  {
    id: 's31', seq: 31, title: 'Water\'s Anomalous Properties', loc: 'Physics', cat: 'Process', biome: 'physics', bg: '#0a1a2a', type: 'eco',
    ayah: 'And We made from water every living thing. (21:30)',
    names: ['musawwir', 'hayy', 'rahman'],
    encounter: 'The most anomalous substance. 104.5° bond angle creates: floating ice (life survives winter), high heat capacity (climate moderation), universal solvent (nutrient transport), cohesion (water climbs 190ft trees).',
    threeQ: {
      is: 'Yes — water\'s anomalous properties exist as physical phenomena, an Ayah of Al-Musawwir, and a trust placed in every molecule.',
      what: 'ONE molecule with specific measurements. From 104.5° bond angle flows: floating ice, thermal stability, solvent capacity, cohesion.',
      when: 'Since the first water molecule. The anomalies are not accidents — they are the conditions that make life possible.',
    },
    names_detail: [
      { name: 'Al-Musawwir', ar: 'المصوّر', why: 'ONE molecule with specific measurements. From 104.5° bond angle flows: floating ice, thermal stability, solvent capacity, cohesion. Planetary consequences from angstrom-level fashioning.' },
      { name: 'Al-Hayy', ar: 'الحي', why: 'And We made from water every living thing. The anomalies ARE the conditions that make life possible. Without them, lifeless ice-locked rock.' },
    ],
    sunnat: [
      'Solar energy evaporates water',
      'Vapor rises, cools, condenses',
      'Rain falls by measure',
      'Water infiltrates soil, dissolves nutrients (universal solvent)',
      'Capillary action draws water up through trees (cohesion)',
      'Water exits leaves through transpiration',
      'Winter: ice forms on surface, FLOATS (insulates life below)',
      'Spring: ice melts, liquid already alive beneath',
    ],
    zahir: 'Water in a glass',
    batin: 'The most consequential taqdir may be invisible (a bond angle determines whether life exists on a planet). The most familiar substance is the most extraordinarily fashioned.',
    haqq: 'The most consequential taqdir may be invisible (a bond angle determines whether life exists on a planet). The most familiar substance is the most extraordinarily fashioned.',
    amanah: 'Water contamination in every city degrades the most precisely fashioned substance in creation. Every water quality program is Islah at the molecular level.',
    bridges: [
      { id: 's2', label: 'Wetland Filtration (purifying what water dissolved)', type: 'cycle' },
      { id: 's22', label: 'Tidal Rhythm (water\'s thermal mass drives tidal mercy)', type: 'cycle' },
    ],
  },
  {
    id: 's32', seq: 32, title: 'Flight', loc: 'Atmospheric', cat: 'Process', biome: 'cross_biome', bg: '#2a2a3a', type: 'eco',
    ayah: 'Not holds them except Ar-Rahman, The Most Compassionate. Indeed, He of every thing All-Seer. (67:19)',
    names: ['rahman', 'basir', 'malik'],
    encounter: 'The Quran does NOT mention wings, flapping, aerodynamics, or lift. It mentions only: Ar-Rahman holds. The names we\'ve named are not the relevant cause.',
    threeQ: {
      is: 'Yes — flight exists as a physical phenomenon, an Ayah of Ar-Rahman, and a trust placed in every bird.',
      what: 'The Quran\'s framing: Ar-Rahman holds. Not: wings lift. The zahir is aerodynamics. The haqq is mercy.',
      when: 'Every moment a bird is in flight. Every thermal. Every migration. Ar-Rahman holds continuously.',
    },
    names_detail: [
      { name: 'Ar-Rahman', ar: 'الرحمن', why: 'The bird is fragile, exposed, vulnerable in open air with nothing beneath it. Ar-Rahman holds the vulnerable. That is what you see every time you look up.' },
      { name: 'Al-Basir', ar: 'البصير', why: 'You see wings, flapping, air. Al-Basir sees the mercy, the decree, the taqdir. The IRL Lens attempts basirah.' },
    ],
    sunnat: [
      'Brain initiates wingbeat (electromagnetic nerve signals)',
      'Flight muscles contract (protein from Ar-Razzaq\'s provision)',
      'Wing moves through air (airfoil taqdir from Al-Musawwir)',
      'Lift exceeds weight (Ar-Rahman holds against Al-Jabbar\'s gravity)',
      'Bird adjusts (sensory processing by Al-Khabir)',
      'Bird soars on thermals (An-Nur created the thermal)',
      'At every step: the zahir yields to deeper batin. At the deepest: Ar-Rahman holds.',
    ],
    zahir: 'A bird in flight',
    batin: 'The names we\'ve named for processes we perceive are not the primary causes. You know HOW the bird flies. Now you know WHO holds it.',
    haqq: 'The names we\'ve named for processes we perceive are not the primary causes. You know HOW the bird flies. Now you know WHO holds it.',
    amanah: 'Habitat destruction, light pollution, glass buildings, pesticides — all disrupt Ar-Rahman\'s mercy. The bird\'s flight is not its achievement to protect; it is Ar-Rahman\'s mercy to maintain.',
    bridges: [
      { id: 's10', label: 'Monarch Migration (continental flight)', type: 'process' },
      { id: 's27', label: 'Electromagnetism (the force that makes wings solid and air viscous)', type: 'process' },
    ],
  },
  {
    id: 's33', seq: 33, title: 'Quantum Mechanics', loc: 'Quantum', cat: 'Process', biome: 'quantum', bg: '#0a0a1a', type: 'eco',
    ayah: 'With Him are the keys of the unseen; none knows them except Him. (6:59)',
    names: ['qadir', 'batin', 'ahad', 'khabir'],
    encounter: 'The batin of the batin. Superposition: ALL possible states simultaneously until observed. Entanglement: what was unified remains unified regardless of distance. Chloroplasts achieve 95% efficiency through quantum coherence.',
    threeQ: {
      is: 'Yes — quantum mechanics exists as a physical framework, an Ayah of Al-Batin, and a trust placed in every observation.',
      what: 'Superposition: all states simultaneously until observed. Entanglement: separation is not real at the fundamental level. Quantum coherence: 95% photosynthesis efficiency.',
      when: 'Since the first quantum fluctuation. The foundation of material reality is probabilistic. This has always been true.',
    },
    names_detail: [
      { name: 'Al-Qadir', ar: 'القدرة', why: 'Power expressed through probability, not determinism. Sustaining reality on a foundation of indeterminate possibility is deeper power than certainty requires.' },
      { name: 'Al-Batin', ar: 'الباطن', why: 'The most fundamental layer is hidden from direct observation — not because instruments fail, but because observing CHANGES the system. Structural unobservability.' },
      { name: 'Al-Ahad', ar: 'الأحد', why: 'Entanglement: what was unified remains unified. Distance does not break what entanglement joined. Tawhid in physics.' },
    ],
    sunnat: [
      'Quantum fluctuation — virtual particles in vacuum',
      'Quantum tunneling (enables stellar fusion)',
      'Quantum coherence (enables photosynthesis at 95%)',
      'Measurement collapses superposition (ghayb→shahādah)',
      'Entanglement maintained across distance (Al-Ahad)',
    ],
    zahir: 'The solid, predictable world around you',
    batin: 'The foundation of material reality is probabilistic. The act of observation participates in what is observed. Separation is not real at the fundamental level.',
    haqq: 'The foundation of material reality is probabilistic. The act of observation participates in what is observed. Separation is not real at the fundamental level. The IRL Lens reaches its material limit here. Beyond: the Amr. Kun.',
    amanah: 'The recognition that human knowledge has a floor. The Lens was designed to lead from observation through recognition to the limit, where recognition becomes submission.',
    bridges: [
      { id: 's3', label: 'Photosynthesis: quantum coherence enables 95% efficiency', type: 'process' },
      { id: 's28', label: 'Strong Nuclear: quantum tunneling enables stellar fusion', type: 'process' },
    ],
  },
];
